import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { AlertTriangle, Loader2, LogOut } from "lucide-react";
import {
  endDelegation,
  getCurrentDelegation,
  startDelegation,
  type DelegationMode,
  type DelegationState,
} from "@/lib/admin-api";
import { useGroceryStore } from "@/lib/grocery-store";
import { usePlateTalkStore } from "@/lib/plate-talk-store";

const INACTIVE: DelegationState = {
  active: false,
  mode: null,
  subjectUserId: null,
  subjectPseudonym: null,
  expiresAt: null,
};

interface DelegationContextValue {
  delegation: DelegationState;
  isLoading: boolean;
  isChanging: boolean;
  error: string | null;
  begin: (subjectUserId: number, mode: DelegationMode) => Promise<void>;
  end: () => Promise<void>;
}

const DelegationContext = createContext<DelegationContextValue | null>(null);

function normalizeDelegation(value: unknown): DelegationState {
  const raw = (value && typeof value === "object" && "delegation" in value)
    ? (value as { delegation?: unknown }).delegation
    : value;
  if (!raw || typeof raw !== "object") return INACTIVE;
  const state = raw as Partial<DelegationState> & {
    state?: string;
    subject?: { id?: number; pseudonym?: string };
    pseudonym?: string;
  };
  if (!state.active && state.state !== "active") return INACTIVE;
  return {
    active: true,
    mode: state.mode === "coach" ? "coach" : "client",
    subjectUserId: typeof state.subjectUserId === "number"
      ? state.subjectUserId
      : typeof state.subject?.id === "number" ? state.subject.id : null,
    subjectPseudonym: typeof state.subjectPseudonym === "string"
      ? state.subjectPseudonym
      : typeof state.pseudonym === "string" ? state.pseudonym
      : typeof state.subject?.pseudonym === "string" ? state.subject.pseudonym
      : "Anonymous user",
    expiresAt: typeof state.expiresAt === "string" ? state.expiresAt : null,
  };
}

export function DelegationProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const grocery = useGroceryStore();
  const plateTalk = usePlateTalkStore();
  const [delegation, setDelegation] = useState(INACTIVE);
  const [isLoading, setIsLoading] = useState(true);
  const [isChanging, setIsChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const identityRef = useRef<string | null>(null);
  const groceryRef = useRef(grocery);
  const plateTalkRef = useRef(plateTalk);
  groceryRef.current = grocery;
  plateTalkRef.current = plateTalk;

  const clearIdentityState = useCallback(() => {
    queryClient.clear();
    groceryRef.current.reset();
    plateTalkRef.current.abort();
    plateTalkRef.current.setMessages([]);
    plateTalkRef.current.setActiveConversationId(null);
  }, [queryClient]);

  const apply = useCallback((nextValue: unknown) => {
    const next = normalizeDelegation(nextValue);
    const identity = next.active ? `${next.mode}:${next.subjectUserId ?? next.subjectPseudonym}` : "admin";
    if (identityRef.current !== null && identityRef.current !== identity) clearIdentityState();
    identityRef.current = identity;
    setDelegation(next);
  }, [clearIdentityState]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      apply(INACTIVE);
      setIsLoading(false);
      return;
    }
    let current = true;
    setIsLoading(true);
    getCurrentDelegation()
      .then((result) => current && apply(result))
      .catch(() => current && apply(INACTIVE))
      .finally(() => current && setIsLoading(false));
    return () => { current = false; };
  }, [apply, isLoaded, isSignedIn]);

  const begin = useCallback(async (subjectUserId: number, mode: DelegationMode) => {
    setIsChanging(true);
    setError(null);
    try {
      apply(await startDelegation(subjectUserId, mode));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not change view.");
      throw cause;
    } finally {
      setIsChanging(false);
    }
  }, [apply]);

  const end = useCallback(async () => {
    setIsChanging(true);
    setError(null);
    try {
      apply(await endDelegation());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not return to administrator view.");
      throw cause;
    } finally {
      setIsChanging(false);
    }
  }, [apply]);

  return (
    <DelegationContext.Provider value={{ delegation, isLoading, isChanging, error, begin, end }}>
      {isLoading ? (
        <div data-testid="delegation-bootstrap-loading" className="flex min-h-screen items-center justify-center bg-background">
          <Loader2 aria-label="Restoring account view" className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : children}
    </DelegationContext.Provider>
  );
}

export function useDelegation() {
  const value = useContext(DelegationContext);
  if (!value) throw new Error("useDelegation must be used within DelegationProvider");
  return value;
}

export function DelegationBanner() {
  const { delegation, isChanging, error, end } = useDelegation();
  const [, setLocation] = useLocation();
  if (!delegation.active) return null;
  const expires = delegation.expiresAt
    ? new Date(delegation.expiresAt).toLocaleString()
    : "at the end of this session";
  return (
    <section
      aria-label="Delegated access is active"
      data-testid="delegation-banner"
      className="sticky top-0 z-[100] border-b-2 border-amber-300 bg-amber-400 px-3 py-2 text-slate-950 shadow-lg"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2" data-testid="status-delegation">
          <AlertTriangle aria-hidden className="h-4 w-4 shrink-0" />
          <strong>{delegation.mode === "coach" ? "Coach mode" : "Client mode"}</strong>
          <span>
            Viewing as <span className="font-mono font-semibold">{delegation.subjectPseudonym}</span>
            <span> · expires {expires}</span>
          </span>
        </div>
        <button
          type="button"
          data-testid="button-return-admin"
          disabled={isChanging}
          onClick={() => void end().then(() => setLocation("/admin"))}
          className="inline-flex min-h-9 items-center gap-2 rounded-md bg-slate-950 px-3 py-1.5 font-semibold text-white disabled:opacity-60"
        >
          {isChanging ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
          Return to Admin
        </button>
        {error && <p role="alert" className="w-full text-xs font-medium">{error}</p>}
      </div>
    </section>
  );
}